import React from 'react';
import { useTranslation } from 'react-i18next';
import { SideNav, SideNavGroup, SideNavItem, Button } from '@origo/origo-ui';
import {
    NoteIcon,
    ListChecksIcon,
    GitBranchIcon,
    FileTextIcon,
    ScalesIcon,
    ArrowLeftIcon,
    FloppyDiskIcon,
} from '@phosphor-icons/react';
import styles from './PlanDetailShell.module.css';

export type PlanTab = 'kart' | 'info' | 'behandlinger' | 'planforhold' | 'dokumenter' | 'dispensasjoner';

interface PlanDetailShellProps {
    activeTab: PlanTab;
    onTabChange: (tab: PlanTab) => void;
    onBackToMap: () => void;
    /** Plannavn vist i topp-raden over SideNav. */
    planTitle?: string;
    /** Top-level "Lagre plan"-handler — stub fra Håvard inntil data er koblet på. */
    onSavePlan?: () => void;
    children: React.ReactNode;
}

const PlanDetailShell: React.FC<PlanDetailShellProps> = ({
    activeTab,
    onTabChange,
    onBackToMap,
    planTitle,
    onSavePlan,
    children,
}) => {
    const { t } = useTranslation();

    const handleSelect = (id: string) => {
        if (id === 'kart') {
            onBackToMap();
            return;
        }
        onTabChange(id as PlanTab);
    };

    return (
        <div className={styles.shell}>
            <a href="#planregister-content" className={styles.skipLink}>
                {t('common.skipToContent')}
            </a>
            <div className={styles.topbar}>
                <div className={styles.topbarLeft}>
                    <button type="button" className={styles.backLink} onClick={onBackToMap}>
                        <ArrowLeftIcon size={16} weight="duotone" aria-hidden />
                        <span>{t('planDetail.backToSearch')}</span>
                    </button>
                    {planTitle && (
                        <span className={styles.planTitle} title={planTitle}>
                            {planTitle}
                        </span>
                    )}
                </div>
                {onSavePlan && (
                    <Button
                        variant="primary"
                        onClick={onSavePlan}
                        leftIcon={<FloppyDiskIcon size={16} weight="duotone" aria-hidden />}
                    >
                        {t('planDetail.savePlan')}
                    </Button>
                )}
            </div>
            <div className={styles.body}>
                <aside className={styles.aside}>
                    <SideNav activeId={activeTab} onSelect={handleSelect}>
                        <SideNavGroup>
                            <SideNavItem id="info" icon={<NoteIcon size={20} weight="duotone" aria-hidden />}>
                                {t('planDetail.tabs.info')}
                            </SideNavItem>
                            <SideNavItem id="behandlinger" icon={<ListChecksIcon size={20} weight="duotone" aria-hidden />}>
                                {t('planDetail.tabs.behandlinger')}
                            </SideNavItem>
                            <SideNavItem id="planforhold" icon={<GitBranchIcon size={20} weight="duotone" aria-hidden />}>
                                {t('planDetail.tabs.planforhold')}
                            </SideNavItem>
                            <SideNavItem id="dokumenter" icon={<FileTextIcon size={20} weight="duotone" aria-hidden />}>
                                {t('planDetail.tabs.dokumenter')}
                            </SideNavItem>
                            <SideNavItem id="dispensasjoner" icon={<ScalesIcon size={20} weight="duotone" aria-hidden />}>
                                {t('planDetail.tabs.dispensasjoner')}
                            </SideNavItem>
                        </SideNavGroup>
                    </SideNav>
                </aside>
                <main id="planregister-content" className={styles.content} tabIndex={-1}>
                    {children}
                </main>
            </div>
        </div>
    );
};

export default PlanDetailShell;
